/**
 * AvatarPiP — the live avatar's face video as a Google-Meet-style PiP card.
 *
 * Used by DemoView's chat sidebar (FLO-545): one component owns the <video>
 * attachment, the speaker-mute sync, and the GPU-isolation styles (FLO-110).
 * The onboarding surface renders its avatar through WebsiteAgentChat's hero
 * slot instead (FLO-51 parity). Renders nothing when there is no live
 * stream — a no-avatar session is pixel-identical to one where this
 * component isn't mounted at all.
 */

import React, { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import { avatarPiPFrame, type VideoDimensions } from './demoLayout';
import { AvatarVideo } from './AvatarVideo';

// Mirrors DemoView's warm palette tokens (module-local there, so re-declared
// rather than imported). The accent vars are set on the DemoView root by
// deriveAccentVars; surfaces that don't set them (the onboarding sidebar)
// fall back to the Floe coral.
const COLOR_TEXT = '#F5F0EB';
const COLOR_ACCENT = 'var(--floe-accent, #FF5C35)';
const COLOR_ACCENT_GLOW = 'var(--floe-accent-glow, rgba(255, 92, 53, 0.35))';

interface AvatarPiPProps {
  /** The live avatar video track. */
  stream?: MediaStream | null;
  /**
   * Silent idle loop shown while the live track is absent — either before it
   * arrives or after an idle swap has retired it. Without one, a dropped track
   * removes the whole card and the sidebar reflows around the hole.
   */
  idleLoopUrl?: string;
  /** Still frame behind the loop, and the reduced-motion presentation. */
  posterUrl?: string;
  /**
   * Retained for API compatibility. The shared renderer is always hard-muted;
   * SDK-owned remote <audio> elements remain the only voice surface and apply
   * the speaker preference themselves.
   */
  speakerMuted?: boolean;
}

export const AvatarPiP: React.FC<AvatarPiPProps> = ({ stream, idleLoopUrl, posterUrl }) => {
  // FLO-545 review: the stream's intrinsic size. The frame used to hardcode
  // 16:9, but pipecat pins the Anam stream to 768×1152 portrait — a 16:9
  // `cover` crop of that shows about a third of the face. Null until
  // `loadedmetadata` reports (and across a swap to a DIFFERENT live stream),
  // which renders the 16:9 default; see avatarPiPFrame for the geometry.
  const [dims, setDims] = useState<VideoDimensions | null>(null);
  // Only a different live stream has an unknown shape. The idle handover
  // (stream -> null -> same stream) is the SAME presenter, so discarding the
  // measured frame there would snap the card from the avatar's 768x1152 to the
  // 16:9 default and back — a hard geometry jump underneath the cross-fade that
  // exists precisely to make that handover invisible.
  const lastLiveStreamIdRef = useRef<string | null>(null);
  useEffect(() => {
    const id = stream?.id ?? null;
    if (id !== null && id !== lastLiveStreamIdRef.current) setDims(null);
    if (id !== null) lastLiveStreamIdRef.current = id;
  }, [stream]);
  const reducedMotion = usePrefersReducedMotion();
  // A dead or blocked media URL must not leave a permanently blank card. Track
  // the exact URL that failed so a later config change gets a fresh attempt
  // rather than being suppressed by a stale flag.
  const [failedLoopUrl, setFailedLoopUrl] = useState<string | null>(null);
  const [failedPosterUrl, setFailedPosterUrl] = useState<string | null>(null);

  // A live track wins; failing that, anything static we can show keeps the card
  // mounted so an idle swap is a cross-fade rather than a layout collapse.
  const rawIdle = idleLoopUrl?.trim() || undefined;
  const rawPoster = posterUrl?.trim() || undefined;
  const posterSource = rawPoster && rawPoster !== failedPosterUrl ? rawPoster : undefined;
  // Reduced motion drops the loop and keeps the still, matching every other
  // avatar surface.
  const idleSource =
    rawIdle && rawIdle !== failedLoopUrl && !reducedMotion ? rawIdle : undefined;
  if (!stream && !idleSource && !posterSource) return null;
  const isLive = !!stream;

  // Wrapped in a padded slot so the framed video doesn't run edge-to-edge
  // against the sidebar. The inner element keeps the rounded border + faint
  // chrome so the avatar reads as "a window into the demo," consistent with
  // DemoView's main screencast frame.
  return (
    <div style={{
      padding: '14px 14px 4px',
      flexShrink: 0,
    }}>
      <div style={{
        // Ratio-derived box (FLO-545 review): 16:9 until metadata reports,
        // then the stream's own shape, height-capped so the Anam portrait
        // stream is a centered tile rather than the whole sidebar.
        ...avatarPiPFrame(dims),
        background: 'transparent',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 12,
        border: 'none',
      }}>
        <AvatarVideo
          stream={stream ?? null}
          src={isLive ? undefined : idleSource}
          poster={posterSource}
          reducedMotion={!isLive && reducedMotion}
          onError={() => {
            // Only the static sources can be retired this way; a failing live
            // track is the SDK's to clear.
            if (isLive) return;
            if (idleSource) setFailedLoopUrl(idleSource);
            else if (posterSource) setFailedPosterUrl(posterSource);
          }}
          objectFit="contain"
          onDimensions={(next) =>
            setDims((current) =>
              current && current.width === next.width && current.height === next.height
                ? current
                : next,
            )
          }
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            // Own GPU layer (FLO-110): the avatar lives in a chat sidebar,
            // which repaints on every streamed transcript token — isolate it
            // so those repaints don't re-rasterize the avatar's decoded video.
            transform: 'translateZ(0)',
            willChange: 'transform',
            backfaceVisibility: 'hidden',
            contain: 'paint',
          }}
        />
        <div lang="en" style={{
          position: 'absolute', bottom: 8, left: 8,
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '3px 8px', borderRadius: 999,
          background: 'rgba(12,10,9,0.7)',
          backdropFilter: 'blur(8px)',
          fontSize: 10, color: COLOR_TEXT, fontWeight: 600,
          letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>
          <span style={{
            width: 5, height: 5, borderRadius: '50%',
            background: isLive ? COLOR_ACCENT : 'rgba(245,240,235,0.45)',
            boxShadow: isLive ? `0 0 8px ${COLOR_ACCENT_GLOW}` : 'none',
          }} />
          {/* Never claim "Live" over the idle loop — the chip is the only thing
              on this card telling the viewer whether the face is real-time. */}
          {isLive ? 'Live' : 'Idle'}
        </div>
      </div>
    </div>
  );
};
